import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { NotificationHubService } from 'app/layout/common/notifications/notification-hub.service';
import {
    Notification,
    NotificationFilter,
} from 'app/layout/common/notifications/notifications.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    styles: [
        `
            .notification-live-card {
                animation: notification-live-card-in 220ms ease-out;
            }

            .notification-toast-timer {
                animation: notification-toast-timer-shrink 5s linear forwards;
            }

            .notification-live-card:hover .notification-toast-timer {
                animation-play-state: paused;
            }

            @keyframes notification-live-card-in {
                from {
                    opacity: 0;
                    transform: translateY(-8px) scale(0.98);
                }
                to {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                }
            }

            @keyframes notification-toast-timer-shrink {
                from {
                    transform: scaleX(1);
                }
                to {
                    transform: scaleX(0);
                }
            }
        `,
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        NgClass,
        DatePipe,
    ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private readonly _destroyRef = inject(DestroyRef);
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel')
    private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[] = [];
    filteredNotifications: Notification[] = [];
    toastNotifications: Notification[] = [];
    unreadCount = 0;
    totalCount = 0;
    activeFilter: NotificationFilter = 'all';
    loading = false;

    private readonly _toastDurationMs = 5000;
    private _overlayRef: OverlayRef;
    private _toastTimers = new Map<
        string,
        {
            timeoutId: ReturnType<typeof setTimeout> | null;
            remainingMs: number;
            startedAt: number | null;
        }
    >();
    private _audioContext: AudioContext | null = null;
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _notificationHub: NotificationHubService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _router: Router
    ) {}

    ngOnInit(): void {
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) => {
                this.notifications = notifications ?? [];
                this._applyFilter();
                this._changeDetectorRef.markForCheck();
            });

        this._notificationsService.unreadCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((count: number) => {
                this.unreadCount = count;
                this._changeDetectorRef.markForCheck();
            });

        this._notificationsService.totalCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((count: number) => {
                this.totalCount = count;
                this._changeDetectorRef.markForCheck();
            });

        this._notificationHub.liveNotification$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notification: Notification) => {
                this._showToast(notification);
            });

        this._notificationsService.getAll().pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
        void this._notificationHub.start();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        this._clearToastTimers();

        if (this._audioContext) {
            void this._audioContext.close();
            this._audioContext = null;
        }

        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    get allCount(): number {
        return this.totalCount || this.notifications.length;
    }

    get displayUnreadCount(): number {
        const localUnread = this.notifications.filter(
            (notification) => !notification.read
        ).length;

        return Math.max(this.unreadCount, localUnread);
    }

    get displayReadCount(): number {
        return this.notifications.filter((notification) => notification.read)
            .length;
    }

    openPanel(): void {
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        if (!this._overlayRef) {
            this._createOverlay();
        }

        this._overlayRef.attach(
            new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
        );

        this._changeDetectorRef.detectChanges();
        this._loadNotifications();
    }

    closePanel(): void {
        this._overlayRef.detach();
    }

    setFilter(filter: NotificationFilter): void {
        this.activeFilter = filter;
        this._applyFilter();
        this._changeDetectorRef.markForCheck();
    }

    toggleExpand(notification: Notification, event?: Event): void {
        event?.stopPropagation();
        notification.expanded = !notification.expanded;
        this._changeDetectorRef.detectChanges();
    }

    isExpanded(notification: Notification): boolean {
        return !!notification.expanded;
    }

    openNotification(notification: Notification, event?: Event): void {
        event?.stopPropagation();
        event?.preventDefault();

        const finish = (): void => {
            notification.expanded = false;
            this.closePanel();

            if (notification.link) {
                void this._router.navigateByUrl(notification.link);
            }

            this._changeDetectorRef.markForCheck();
        };

        if (!notification.read) {
            this._notificationsService.markAsRead(notification.id).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
                next: () => {
                    notification.read = true;
                    this._applyFilter();
                    finish();
                },
                error: () => finish(),
            });
            return;
        }

        finish();
    }

    dismiss(notification: Notification, event: Event): void {
        event.stopPropagation();
        this._notificationsService.archive(notification.id).pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
    }

    markAllAsRead(): void {
        if (this.displayUnreadCount === 0) {
            return;
        }

        this._notificationsService.markAllAsRead('bell').pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
            next: () => {
                this._changeDetectorRef.markForCheck();
            },
        });
    }

    clearAll(): void {
        if (!this.notifications.length) {
            return;
        }

        this._notificationsService.deleteAll('bell').pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
    }

    dismissToast(id: string, event?: Event): void {
        event?.stopPropagation();
        event?.preventDefault();

        this._clearToastTimer(id);

        this.toastNotifications = this.toastNotifications.filter(
            (notification) => notification.id !== id
        );
        this._changeDetectorRef.markForCheck();
    }

    pauseToast(id: string): void {
        const timer = this._toastTimers.get(id);
        if (!timer || timer.startedAt == null) {
            return;
        }

        if (timer.timeoutId) {
            clearTimeout(timer.timeoutId);
            timer.timeoutId = null;
        }

        const elapsed = Date.now() - timer.startedAt;
        timer.remainingMs = Math.max(0, timer.remainingMs - elapsed);
        timer.startedAt = null;
    }

    resumeToast(id: string): void {
        const timer = this._toastTimers.get(id);
        if (!timer || timer.startedAt != null) {
            return;
        }

        if (timer.remainingMs <= 0) {
            this.dismissToast(id);
            return;
        }

        this._armToastTimeout(id, timer);
    }

    onToastClick(notification: Notification): void {
        this.dismissToast(notification.id);

        const navigate = (): void => {
            if (notification.link) {
                void this._router.navigateByUrl(notification.link);
                return;
            }

            this.openPanel();
        };

        if (!notification.read) {
            this._notificationsService.markAsRead(notification.id).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
                next: () => navigate(),
                error: () => navigate(),
            });
            return;
        }

        navigate();
    }

    private _showToast(notification: Notification): void {
        if (!notification?.id) {
            return;
        }

        if (
            this.toastNotifications.some(
                (item) => item.id === notification.id
            )
        ) {
            return;
        }

        this.toastNotifications = [
            notification,
            ...this.toastNotifications,
        ].slice(0, 3);
        this._changeDetectorRef.markForCheck();
        this._playNotificationSound();
        this._startToastTimer(notification.id);
    }

    private _startToastTimer(id: string): void {
        this._clearToastTimer(id);

        const timer = {
            timeoutId: null as ReturnType<typeof setTimeout> | null,
            remainingMs: this._toastDurationMs,
            startedAt: null as number | null,
        };
        this._toastTimers.set(id, timer);
        this._armToastTimeout(id, timer);
    }

    private _armToastTimeout(
        id: string,
        timer: {
            timeoutId: ReturnType<typeof setTimeout> | null;
            remainingMs: number;
            startedAt: number | null;
        }
    ): void {
        timer.startedAt = Date.now();
        timer.timeoutId = setTimeout(() => {
            this.dismissToast(id);
        }, timer.remainingMs);
    }

    private _clearToastTimer(id: string): void {
        const timer = this._toastTimers.get(id);
        if (!timer) {
            return;
        }

        if (timer.timeoutId) {
            clearTimeout(timer.timeoutId);
        }

        this._toastTimers.delete(id);
    }

    private _playNotificationSound(): void {
        try {
            const AudioCtx =
                window.AudioContext ||
                (
                    window as Window & {
                        webkitAudioContext?: typeof AudioContext;
                    }
                ).webkitAudioContext;

            if (!AudioCtx) {
                return;
            }

            if (!this._audioContext || this._audioContext.state === 'closed') {
                this._audioContext = new AudioCtx();
            }

            const ctx = this._audioContext;
            if (ctx.state === 'suspended') {
                void ctx.resume();
            }

            const now = ctx.currentTime;
            const playTone = (
                frequency: number,
                startAt: number,
                duration: number,
                peakGain: number
            ): void => {
                const oscillator = ctx.createOscillator();
                const gain = ctx.createGain();

                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(frequency, startAt);

                gain.gain.setValueAtTime(0.0001, startAt);
                gain.gain.exponentialRampToValueAtTime(
                    peakGain,
                    startAt + 0.02
                );
                gain.gain.exponentialRampToValueAtTime(
                    0.0001,
                    startAt + duration
                );

                oscillator.connect(gain);
                gain.connect(ctx.destination);
                oscillator.start(startAt);
                oscillator.stop(startAt + duration + 0.02);
            };

            // Soft two-tone chime
            playTone(880, now, 0.14, 0.12);
            playTone(1175, now + 0.11, 0.22, 0.1);
        } catch {
            // Ignore autoplay / AudioContext failures
        }
    }

    private _clearToastTimers(): void {
        for (const timer of this._toastTimers.values()) {
            if (timer.timeoutId) {
                clearTimeout(timer.timeoutId);
            }
        }
        this._toastTimers.clear();
    }

    private _loadNotifications(): void {
        this.loading = true;
        this._changeDetectorRef.markForCheck();

        this._notificationsService.getAll().pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
            next: () => {
                this.loading = false;
                this._applyFilter();
                this._notificationsService.getUnreadCount().pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
                    complete: () => this._changeDetectorRef.detectChanges(),
                });
                this._changeDetectorRef.detectChanges();
            },
            error: () => {
                this.loading = false;
                this._changeDetectorRef.detectChanges();
            },
        });
    }

    private _applyFilter(): void {
        switch (this.activeFilter) {
            case 'unread':
                this.filteredNotifications = this.notifications.filter(
                    (notification) => !notification.read
                );
                break;
            case 'read':
                this.filteredNotifications = this.notifications.filter(
                    (notification) => notification.read
                );
                break;
            default:
                this.filteredNotifications = [...this.notifications];
                break;
        }
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._notificationsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        this._overlayRef.backdropClick().pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
            this._overlayRef.detach();
        });
    }
}
